import axios from "axios";
import { version as pkgVersion } from "../../package.json";

export type FeishuAlarmLevel = "warning" | "alarm";
export type FeishuAlarmSource = "http" | "proxy";

export interface FeishuAlarmReport {
    /** alarm 会额外 @所有人，warning 只发消息 */
    level: FeishuAlarmLevel;
    /** 业务标识：固定包名 ... */
    platform: string;
    /** 告警来源模块 */
    source: FeishuAlarmSource;
    /** 出错阶段，如 "POST /api/sns/web/v1/feed" */
    stage: string;
    /** 错误类型，如 HTTP_403 / ECONNRESET / PROXY_UNAVAILABLE */
    errorType: string;
    /** 接口错误码 */
    code?: string | number;
    /** 错误信息 */
    msg?: string;
    /** 请求地址 */
    url?: string;
    /** 消息标题，默认「RPA异常」 */
    title?: string;
}

type FeishuPostNode = { tag: "text"; text: string } | { tag: "at"; user_id: string };
type FeishuPostContent = FeishuPostNode[][];

const RPA_ERROR_WEBHOOK_URL =
    "https://open.xfchat.iflytek.com/open-apis/bot/v2/hook/d202c0dc-5af5-40bc-83ed-abc677caa4a5";

/**
 * 登录态失效不是系统故障：用户重新登录即可恢复，@所有人属于噪音。
 * 各平台文案不统一（"登录已过期" / "登录失效" / "登录状态已失效，请重新登录后重试！"），
 * 所以按关键词匹配而不是精确比较。
 */
const AUTH_EXPIRED_MSG_PATTERN = /登录[^，。,.]{0,4}(过期|失效)|重新登录|未登录/;

/** 同一类错误的最小上报间隔，避免重试风暴把群刷爆 */
const ALARM_THROTTLE_MS = 60 * 1000;
const lastSentAt = new Map<string, number>();

/**
 * 节流表清理阈值：节流 key 含 stage（接口路径），长时间运行的进程里 key 会持续增长，
 * 超过阈值时清掉已过期的条目，避免 Map 无上限膨胀。
 */
const THROTTLE_MAP_MAX_KEYS = 500;

const pruneThrottleMap = (now: number) => {
    if (lastSentAt.size < THROTTLE_MAP_MAX_KEYS) return;
    for (const [key, at] of lastSentAt) {
        if (now - at >= ALARM_THROTTLE_MS) lastSentAt.delete(key);
    }
};

const postFeishuWebhook = (webhookUrl: string, payload: unknown) =>
    axios.post(webhookUrl, payload, {
        headers: {
            "Content-Type": "application/json",
        },
        timeout: 10000,
    });

const buildFeishuPostMessage = (report: FeishuAlarmReport) => {
    const content: FeishuPostContent = [
        [{ tag: "text", text: `平台：${report.platform || "未知平台"}` }],
        [{ tag: "text", text: `版本：${pkgVersion}` }],
        [{ tag: "text", text: `错误信息：${report.msg || "未知错误信息"}` }],
        [{ tag: "text", text: `错误类型：${report.errorType || "未知错误类型"}` }],
        [{ tag: "text", text: `阶段：${report.stage || "未知阶段"}` }],
        [{ tag: "text", text: `等级：${report.level}` }],
        [{ tag: "text", text: `来源：${report.source}` }],
    ];

    if (report.code !== undefined && report.code !== null) {
        content.push([{ tag: "text", text: `接口错误码：${report.code}` }]);
    }
    if (report.url) {
        content.push([{ tag: "text", text: `地址：${report.url}` }]);
    }
    if (report.level === "alarm") {
        content.push([{ tag: "at", user_id: "all" }]);
    }

    return {
        msg_type: "post",
        content: {
            post: {
                zh_cn: {
                    title: report.title || "RPA异常",
                    content,
                },
            },
        },
    };
};

/**
 * 上报错误到飞书群，失败静默：告警链路不能影响主流程。
 * 同一 platform + source + stage + errorType + code 组合在 60s 内只发一条。
 *
 * 节流 key 里必须带 stage：不同接口的同类错误（例如 unread_count 与 personal_info
 * 同时 ECONNABORTED）是两个独立问题，混成一个 key 会让后来的那个被静默吞掉。
 *
 * msg 命中登录失效文案的一律不上报：这类错误要用户自己重新登录，不需要研发介入。
 */
export const reportFeishuAlarm = (report: FeishuAlarmReport): void => {
    try {
        if (report.level !== "alarm") return;
        if (report.msg && AUTH_EXPIRED_MSG_PATTERN.test(report.msg)) return;

        const key = `${report.platform}|${report.source}|${report.stage}|${report.errorType}|${report.code ?? ""}`;
        const now = Date.now();
        const last = lastSentAt.get(key);
        if (last && now - last < ALARM_THROTTLE_MS) return;
        pruneThrottleMap(now);
        lastSentAt.set(key, now);

        postFeishuWebhook(RPA_ERROR_WEBHOOK_URL, buildFeishuPostMessage(report)).catch(() => {});
    } catch {
        // 上报失败不能影响主流程。
    }
};
